class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));
    this.initializePrices();
  }

  getCurrencySymbol() {
    const currentUrl = window.location.href.toLowerCase();
    if (currentUrl.includes('sundrop.studio/en-int')) {
      return '$';
    } else if (currentUrl.includes('sundrop.studio/en-eu')) {
      return '€';
    }
    return '£';
  }

  formatPrice(amount, currencySymbol) {
    const formattedAmount = (amount / 100).toFixed(2);
    // For USD and EUR, place symbol before number
    if (currencySymbol === '$' || currencySymbol === '€') {
      return `${currencySymbol}${formattedAmount}`;
    }
    // For GBP, keep symbol before number
    return `${currencySymbol}${formattedAmount}`;
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer')?.dataset.id,
        selector: '.js-contents',
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
          if (elementToReplace && parsedState.sections[section.section]) {
            elementToReplace.innerHTML =
              this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          }
        }));

        this.updateLiveRegions(line, parsedState.item_count);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }

        this.updatePrices(parsedState.original_total_price, parsedState.total_price);
        this.updateProgressBar(parsedState.total_price, parsedState.item_count);

        this.disableLoading();
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.disableLoading();
      });
  }

  updatePrices(originalTotal, currentTotal) {
    const originalPriceElement = document.querySelector('.totals__subtotal-value s');
    const currentPriceElement = document.querySelector('.totals__subtotal-value');
    const currencySymbol = this.getCurrencySymbol();

    if (originalTotal && originalTotal != currentTotal) {
      const originalPriceFormatted = this.formatPrice(originalTotal, currencySymbol);
      if (originalPriceElement) {
        originalPriceElement.textContent = originalPriceFormatted;
        originalPriceElement.style.display = 'inline';
      } else {
        const newOriginalPrice = document.createElement('s');
        newOriginalPrice.textContent = originalPriceFormatted;
        newOriginalPrice.style.cssText = "color: red; margin-right: 15px;";
        currentPriceElement.prepend(newOriginalPrice);
      }
    } else if (originalPriceElement) {
      originalPriceElement.style.display = 'none';
    }

    const currentPriceFormatted = this.formatPrice(currentTotal, currencySymbol);
    currentPriceElement.lastChild.textContent = currentPriceFormatted;
  }

  updateProgressBar(cartTotal, itemCount) {
    const progressWrapper = document.getElementById('cart-progress-wrapper');
    if (!progressWrapper) return;

    const moneyFormat = progressWrapper.dataset.moneyFormat.replace('£', this.getCurrencySymbol());
    const progressThreshold = parseInt(progressWrapper.dataset.threshold, 10);
    const preGoalMessageTemplate = progressWrapper.dataset.preGoalMessageTemplate;
    const postGoalMessage = progressWrapper.dataset.postGoalMessage;

    const progressBar = document.getElementById('cart-progress-bar');
    const goalMessageElement = document.querySelector('.goal-message');

    if (itemCount === 0 || cartTotal === 0) {
      if (progressWrapper) progressWrapper.style.display = 'none';
      if (goalMessageElement) goalMessageElement.style.display = 'none';
      return;
    }

    progressWrapper.style.display = 'block';
    
    if (progressBar) {
      progressBar.style.display = 'block';
      const progressPercentage = Math.min((cartTotal / progressThreshold) * 100, 100);
      progressBar.style.width = `${progressPercentage}%`;
      progressWrapper.classList.toggle('full', progressPercentage >= 100);
    }

    if (goalMessageElement) {
      goalMessageElement.style.display = 'block';
      let remainingForGoal = Math.max(progressThreshold - cartTotal, 0);
      const remainingAmount = remainingForGoal / 100;
      const remainingAmountFormatted = moneyFormat.replace('{{amount}}', remainingAmount.toFixed(2));
      const preGoalMessage = preGoalMessageTemplate.replace('[remainingForGoalFormatted]', remainingAmountFormatted);
      goalMessageElement.innerHTML = remainingForGoal > 0 ? preGoalMessage : postGoalMessage;
    }
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);

      if (lineItemError && quantityElement) {
        lineItemError
          .querySelector('.cart-item__error-text')
          .innerHTML = window.cartStrings.quantityError.replace(
            '[quantity]',
            quantityElement.value
          );
      }
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading() {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
  }

  initializePrices() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.updatePrices(cart.original_total_price, cart.total_price);
        this.updateProgressBar(cart.total_price, cart.items.length);
      });
  }
}

customElements.define('cart-items', CartItems);

class UpsellAddButton extends HTMLElement {
  constructor() {
    super();
    this.queue = Promise.resolve();
    this.addEventListener('click', this.onButtonClick.bind(this));
  }

  async onButtonClick(event) {
    event.preventDefault();
    if (this.disabled) return;

    // Add to queue
    this.queue = this.queue.then(() => this.handleAddToCart());
  }

  async handleAddToCart() {
    // Show loading state
    this.disabled = true;
    const spinner = this.querySelector('.loading-spinner');
    const buttonText = this.querySelector('.button-text');
    if (spinner) spinner.classList.remove('hidden');
    if (buttonText) buttonText.style.opacity = '0.5';

    try {
      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(this.dataset.variantid),
            quantity: 1
          }]
        })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      // Force cart drawer refresh
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) {
        // Get fresh cart state
        const cartData = await fetch('/cart.js').then(res => res.json());

        // Update cart count
        document.querySelectorAll('.cart-count-bubble').forEach(el => {
          el.textContent = cartData.item_count;
        });

        // Remove empty state
        cartDrawerItems.classList.remove('is-empty');
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.classList.remove('is-empty');

        // Trigger cart refresh using existing methods
        const sections = cartDrawerItems.getSectionsToRender?.() || [];
        const body = JSON.stringify({
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname
        });

        const stateResponse = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body }
        });

        const state = await stateResponse.text();
        const parsedState = JSON.parse(state);

        // Update sections
        sections.forEach((section => {
          const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector)
            || document.getElementById(section.id);
          if (elementToReplace && parsedState.sections[section.section]) {
            elementToReplace.innerHTML = cartDrawerItems.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        }));

        // Update prices
        cartDrawerItems.updatePrices?.(parsedState.original_total_price, parsedState.total_price);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      // Reset button state
      this.disabled = false;
      if (spinner) spinner.classList.add('hidden');
      if (buttonText) buttonText.style.opacity = '1';
    }
  }
}

customElements.define('upsell-add-button', UpsellAddButton);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
      }, 300))
    }
  });
}

// Close details elements when clicking outside
var details = [...document.querySelectorAll('details')];
document.addEventListener('click', function(e) {
  if (!details.some(f => f.contains(e.target))) {
    details.forEach(f => f.removeAttribute('open'));
  } else {
    details.forEach(f => !f.contains(e.target) ? f.removeAttribute('open') : '');
  }
});